import { appIsTest, GetApiBaseUrl } from 'src/constants/UrlConstants';
import { GetDDMSchema } from 'src/constants/ApiConstants';
import * as KatalMetrics from '@amzn/katal-metrics';
import { emitApiErrorMetrics, MetricsName } from 'src/metrics';
import { logger } from 'src/logger';
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import {
  IGetSchemaRequest,
  IGetSchemaResponse,
} from 'src/integration/ddm/DDMModel';
import { parseJson } from 'src/utilities/ApiHelper';

export const ddmServiceApi = createApi({
  reducerPath: 'ddmServiceApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: GetApiBaseUrl(),
    }),
  ),
  endpoints: (builder) => ({
    /**
     * GetUserAccounts will fetch the list of UMS accounts available for the user.
     */
    getSchema: builder.query<IGetSchemaResponse, Partial<IGetSchemaRequest>>({
      query: (request) => ({
        url:
          request.isPublicPage && request.isRegulatorCentralApp
            ? GetDDMSchema.getPublicRoute(
                request.clientId!,
                request.schemaKey!,
                request.schemaVersion!,
              )
            : GetDDMSchema.getRoute(
                request.clientId!,
                request.schemaKey!,
                request.schemaVersion!,
              ),
        method: GetDDMSchema.method,
        responseHandler: 'content-type',
        keepUnusedDataFor: appIsTest ? 0 : GetDDMSchema.keepUnusedDataFor,
      }),
      extraOptions: {
        maxRetries: GetDDMSchema.numberOfRetries,
      },
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling getSchema API with request: ${JSON.stringify(request)}`,
          );
          const { data } = await queryFulfilled;
          logger.info(`Response for getSchema API is: ${JSON.stringify(data)}`);
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occurred while fetching getSchema API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(error, GetDDMSchema.metricsPublisher);
          metric.setFailure();
        }
        GetDDMSchema.metricsPublisher.publish(metric);
      },
      transformResponse: (response: IGetSchemaResponse) => {
        const { schemaDefinition } = response;
        const parsedCoreSchemaDefinition = parseJson(
          schemaDefinition.coreSchemaDefinition as string,
        );
        logger.info(
          `Parsed CoreSchemaDefinition: ${JSON.stringify(
            parsedCoreSchemaDefinition,
          )}`,
        );
        const parsedUiSchemaDefinition = parseJson(
          schemaDefinition.uiSchemaDefinition as string,
        );
        logger.info(
          `Parsed UISchemaDefinition: ${JSON.stringify(
            parsedUiSchemaDefinition,
          )}`,
        );
        response.schemaDefinition = {
          ...schemaDefinition,
          coreSchemaDefinition: parsedCoreSchemaDefinition,
          uiSchemaDefinition: parsedUiSchemaDefinition,
        };
        logger.info(
          `Updated Response for getSchema: ${JSON.stringify(response)}`,
        );
        return response;
      },
    }),
  }),
});

/* Automatically generated hooks for each endpoint query */
export const { useGetSchemaQuery, endpoints, middleware } = ddmServiceApi;
